/**
 * Sign in with Apple. There is no Apple SDK on Android/web, so both use
 * Apple's OAuth redirect flow against our Service ID:
 *
 *   1. Open https://appleid.apple.com/auth/authorize in a browser surface
 *      (Custom Tab on Android, popup on web).
 *   2. Apple form_posts the id_token to the main-server callback
 *      (/auth/apple/callback — registered as the Service ID's Return URL).
 *   3. The callback page relays it back to us: deep link into the app
 *      (custom scheme, `app.` state prefix) or postMessage to the opener
 *      (`web.` state prefix). The prefix is how the server picks the relay.
 *   4. We hand the id_token to loginSocial(); the server verifies signature +
 *      audience against Apple's JWKS. The relay itself is untrusted.
 *
 * Native iOS uses the real AuthenticationServices sheet via
 * @capacitor-community/apple-sign-in (required by App Store Guideline 4.8 when
 * we also offer Google). It returns an identity token minted for the app's
 * bundle id, which the server's JWKS verify already accepts (audience array of
 * bundle id + Service ID), so no server change is needed.
 *
 * The `state` round-trip is checked HERE (client-generated, client-verified)
 * so a stray/forged deep link can't complete someone else's sign-in.
 */

import { Capacitor, type PluginListenerHandle } from '@capacitor/core';
import { App } from '@capacitor/app';
import { Browser } from '@capacitor/browser';
import { SignInWithApple } from '@capacitor-community/apple-sign-in';

export interface AppleSignInResult {
  token: string;
  tokenType: 'id';
}

const SERVICE_ID = 'com.gritplaygames.streetdicelegends.signin';
// Native iOS mints the identity token for the app's BUNDLE id (not the web
// Service ID). The server accepts both as audience, so this just needs to match
// the real bundle id (capacitor.config appId).
const BUNDLE_ID = 'com.gritplaygames.streetdicelegends';
const AUTHORIZE_URL = 'https://appleid.apple.com/auth/authorize';
// Apple only accepts the HTTPS Return URLs registered on the Service ID —
// localhost dev servers can't be registered, so this is always prod.
const CALLBACK_ORIGIN = 'https://api.streetdicelegends.com';
const REDIRECT_URI = `${CALLBACK_ORIGIN}/auth/apple/callback`;

function randomToken(): string {
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
}

function authorizeUrl(state: string): string {
  const params = new URLSearchParams({
    client_id: SERVICE_ID,
    redirect_uri: REDIRECT_URI,
    response_type: 'code id_token',
    response_mode: 'form_post', // required by Apple when requesting name/email
    scope: 'name email',
    state,
    nonce: randomToken(),
  });
  return `${AUTHORIZE_URL}?${params.toString()}`;
}

/** Android (Capacitor): Custom Tab → server callback → custom-scheme deep link. */
async function appleSignInApp(): Promise<AppleSignInResult | null> {
  const state = `app.${randomToken()}`;

  return new Promise<AppleSignInResult | null>((resolve) => {
    let settled = false;
    let urlSub: PluginListenerHandle | null = null;
    let finishSub: PluginListenerHandle | null = null;

    const finish = (result: AppleSignInResult | null) => {
      if (settled) return;
      settled = true;
      urlSub?.remove();
      finishSub?.remove();
      Browser.close().catch(() => { /* tab already closed itself */ });
      resolve(result);
    };

    void (async () => {
      urlSub = await App.addListener('appUrlOpen', ({ url }) => {
        // com.gritplaygames.streetdicelegends://apple-signin#id_token=…&state=…
        if (!url.includes('://apple-signin')) return;
        const frag = url.split('#')[1] ?? '';
        const params = new URLSearchParams(frag);
        if (params.get('state') !== state) return; // not this attempt's callback
        const idToken = params.get('id_token');
        finish(idToken ? { token: idToken, tokenType: 'id' } : null);
      });

      // User closed the tab without completing → clean cancel. The grace
      // period lets a racing deep-link (which also closes the tab) win.
      finishSub = await Browser.addListener('browserFinished', () => {
        setTimeout(() => finish(null), 1500);
      });

      await Browser.open({ url: authorizeUrl(state) });
    })().catch(() => finish(null));
  });
}

/** Web (Vite/browser): popup → server callback → postMessage to opener. */
function appleSignInWeb(): Promise<AppleSignInResult | null> {
  const state = `web.${randomToken()}`;
  const popup = window.open(authorizeUrl(state), 'sdl-apple-signin', 'width=600,height=720');
  if (!popup) return Promise.resolve(null); // popup blocked → treat as cancel

  return new Promise<AppleSignInResult | null>((resolve) => {
    let settled = false;
    const finish = (result: AppleSignInResult | null) => {
      if (settled) return;
      settled = true;
      window.removeEventListener('message', onMessage);
      clearInterval(closeWatch);
      resolve(result);
    };

    const onMessage = (e: MessageEvent) => {
      if (e.origin !== CALLBACK_ORIGIN) return; // only our callback page
      const d = e.data as { source?: string; idToken?: string; state?: string } | null;
      if (!d || d.source !== 'sdl-apple-signin' || d.state !== state) return;
      finish(d.idToken ? { token: d.idToken, tokenType: 'id' } : null);
    };
    window.addEventListener('message', onMessage);

    const closeWatch = setInterval(() => {
      if (popup.closed) {
        // Either a real user cancel, or the relay failed (e.g. our origin
        // isn't in the server's webOrigins allowlist). Log so a silent
        // mis-config is diagnosable from the console.
        console.warn('[apple-login] popup closed without delivering a token (cancel, or origin not allowlisted —', window.location.origin, ')');
        finish(null);
      }
    }, 500);
  });
}

/**
 * iOS (Capacitor): native AuthenticationServices sheet via the apple-sign-in
 * plugin. Returns the identity token (aud = bundle id). A user cancel surfaces
 * as ASAuthorizationError.canceled (1001) → treated as a clean null; any other
 * error is rethrown so a real mis-config shows the sign-in-failed modal.
 */
async function appleSignInNativeIOS(): Promise<AppleSignInResult | null> {
  try {
    const { response } = await SignInWithApple.authorize({
      clientId: BUNDLE_ID,
      redirectURI: REDIRECT_URI, // required by the type; unused by the native iOS flow
      scopes: 'name email',
      nonce: randomToken(),
      state: randomToken(),
    });
    return response?.identityToken ? { token: response.identityToken, tokenType: 'id' } : null;
  } catch (e) {
    const code = String((e as { code?: string | number })?.code ?? '');
    const msg = String((e as { message?: string })?.message ?? '');
    if (code === '1001' || /\b1001\b|cancel/i.test(msg)) return null; // user dismissed the sheet
    throw e;
  }
}

/** Sign in with Apple. Resolves null on a clean user cancel. */
export async function appleSignIn(): Promise<AppleSignInResult | null> {
  // iOS: real native sheet (App Store 4.8). Android: Custom Tab OAuth. Web: popup.
  if (Capacitor.getPlatform() === 'ios') return appleSignInNativeIOS();
  return Capacitor.isNativePlatform() ? appleSignInApp() : appleSignInWeb();
}
